/**
 * Talking to the page this application is embedded in.
 *
 * When the builder runs inside a host portal, the host can hand it a token,
 * tell it which theme to use, and say which reporting period the user is
 * looking at. That conversation happens over `postMessage`, which has exactly
 * one dangerous property: **any page can send a message, and any page that
 * embeds this one can read what it sends.**
 *
 * So three rules hold here, without exception:
 *
 *   1. **Incoming messages are checked against an allowlist of exact
 *      origins, in every environment.** Not just production. A check that is
 *      relaxed in development is a check that gets written wrong and stays
 *      wrong, because nobody exercises it until it matters.
 *
 *   2. **The match is exact.** A prefix match on origins is a hole:
 *      `https://portal.example.com.attacker.test` starts with
 *      `https://portal.example.com`, and would pass.
 *
 *   3. **Outgoing messages that carry anything private name their target
 *      origin.** A wildcard target broadcasts to whoever happens to be the
 *      parent. The probes that carry nothing may use a wildcard, and only
 *      those.
 *
 * A parent frame is not trustworthy by virtue of being the parent.
 */

import { config } from '@/app/config'

/** What the host can tell this application. */
export type HostMessage =
  | { readonly kind: 'token'; readonly token: string }
  | { readonly kind: 'signed-out' }
  | { readonly kind: 'theme'; readonly theme: 'light' | 'dark' }
  | { readonly kind: 'reporting-context'; readonly payload: Record<string, unknown> }

type Listener = (message: HostMessage) => void

/** Whether this page is embedded at all. */
export function isEmbedded(): boolean {
  return window.parent !== window
}

/**
 * Whether a message came from somewhere permitted.
 *
 * Exact comparison, and the same comparison everywhere.
 */
function isAllowed(origin: string): boolean {
  return config.embedding.allowedParentOrigins.includes(origin)
}

/**
 * The origin to address the host by, or null when it is not known to be one
 * we may talk to.
 *
 * Derived from the referrer, then confirmed against the allowlist. Anything
 * unconfirmed gets nothing sent to it.
 */
function hostOrigin(): string | null {
  if (!isEmbedded()) return null
  try {
    if (!document.referrer) return null
    const candidate = new URL(document.referrer).origin
    return isAllowed(candidate) ? candidate : null
  } catch {
    return null
  }
}

/** The raw shape a host message arrives in. */
interface RawMessage {
  type?: unknown
  token?: unknown
  theme?: unknown
  app?: unknown
  current?: unknown
  available?: unknown
}

function interpret(raw: RawMessage): HostMessage | null {
  switch (raw.type) {
    case 'AUTH_TOKEN':
    case 'TOKEN_REFRESH':
      return typeof raw.token === 'string' && raw.token ? { kind: 'token', token: raw.token } : null

    case 'LOGOUT':
      return { kind: 'signed-out' }

    case 'THEME_CHANGE':
      // Only the host application's own theme messages, so a browser
      // extension broadcasting the same message name cannot restyle the page.
      if (raw.app !== 'EPM') return null
      return raw.theme === 'light' || raw.theme === 'dark'
        ? { kind: 'theme', theme: raw.theme }
        : null

    case 'REPORTINGS_CONTEXT':
      if (raw.app !== 'EPM') return null
      return {
        kind: 'reporting-context',
        payload: { current: raw.current, available: raw.available },
      }

    default:
      return null
  }
}

/**
 * The bridge to the host page.
 *
 * A class rather than loose functions, because there is real state to own: the
 * listener registration, which must be removed as carefully as it was added.
 */
export class HostBridge {
  private listeners = new Set<Listener>()
  private receive: ((event: MessageEvent<RawMessage>) => void) | null = null

  /**
   * Start listening.
   *
   * Attach this early. A host that announces its theme milliseconds after the
   * frame loads will have finished doing so before a slower start, and the
   * page then renders in the wrong theme until something else prompts it.
   */
  start(): void {
    if (this.receive) return

    this.receive = (event: MessageEvent<RawMessage>): void => {
      if (!isAllowed(event.origin)) return
      if (typeof event.data !== 'object' || event.data === null) return

      const message = interpret(event.data)
      if (!message) return

      for (const listener of this.listeners) listener(message)
    }

    window.addEventListener('message', this.receive)
  }

  stop(): void {
    if (!this.receive) return
    window.removeEventListener('message', this.receive)
    this.receive = null
    this.listeners.clear()
  }

  /** Be told when the host says something. Returns a function to stop. */
  on(listener: Listener): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  /**
   * Ask the host for something.
   *
   * These probes carry nothing private, which is why they may go to a host
   * whose origin has not been confirmed: the worst case is that an unknown
   * parent learns this application would like to know its theme.
   */
  private ask(type: 'REQUEST_THEME' | 'REQUEST_REPORTINGS' | 'REQUEST_TOKEN'): void {
    if (!isEmbedded()) return
    window.parent.postMessage({ type }, hostOrigin() ?? '*')
  }

  requestTheme(): void {
    this.ask('REQUEST_THEME')
  }

  requestReportingContext(): void {
    this.ask('REQUEST_REPORTINGS')
  }

  /**
   * Ask the host to relay a token.
   *
   * The fallback path: normally the application obtains its own token from the
   * portal directly, and only asks the host when that fails.
   */
  requestToken(): void {
    this.ask('REQUEST_TOKEN')
  }

  /**
   * Tell the host something.
   *
   * Refuses to send when the host's origin is unconfirmed. This is the rule
   * the probes above are exempt from, and the reason the exemption is
   * narrow: everything sent through here could be read by whoever received it.
   */
  tell(type: string, payload: Record<string, unknown> = {}): boolean {
    const target = hostOrigin()
    if (!target) return false
    window.parent.postMessage({ type, ...payload }, target)
    return true
  }
}

export const hostBridge = new HostBridge()
